import React from 'react';
import { Bell, Calendar, Target, X, Clock, AlertTriangle, ChevronRight } from 'lucide-react';

/**
 * Banner de notificações para exibir alertas de agenda e missões
 */
const NotificationBanner = React.memo(({
    notifications,
    permission,
    onRequestPermission,
    onDismiss,
    onDismissAll,
    onOpenAgenda,
    onOpenMission
}) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    // Se não houver notificações E permissão já estiver garantida ou negada, não mostra nada
    if ((!notifications || notifications.length === 0) && permission !== 'default') return null;

    const getUrgencyStyles = (urgency) => {
        switch (urgency) {
            case 'danger':
                return {
                    bg: 'bg-red-50 dark:bg-red-900/20',
                    border: 'border-red-200 dark:border-red-800',
                    icon: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
                    text: 'text-red-800 dark:text-red-200',
                    subtext: 'text-red-600 dark:text-red-400'
                };
            case 'warning':
                return {
                    bg: 'bg-orange-50 dark:bg-orange-900/20',
                    border: 'border-orange-200 dark:border-orange-800',
                    icon: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
                    text: 'text-orange-800 dark:text-orange-200',
                    subtext: 'text-orange-600 dark:text-orange-400'
                };
            default:
                return {
                    bg: 'bg-blue-50 dark:bg-blue-900/20',
                    border: 'border-blue-200 dark:border-blue-800',
                    icon: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
                    text: 'text-blue-800 dark:text-blue-200',
                    subtext: 'text-blue-600 dark:text-blue-400'
                };
        }
    };

    const getIcon = (type, urgency) => {
        if (urgency === 'danger') return AlertTriangle;
        if (type === 'agenda') return Calendar;
        if (type.startsWith('mission')) return Target;
        return Bell;
    };

    const handleClick = (notification) => {
        if (notification.type === 'agenda' && onOpenAgenda) {
            onOpenAgenda();
        } else if (notification.type.startsWith('mission') && onOpenMission) {
            onOpenMission();
        }
    };

    const hasMultiple = notifications && notifications.length > 1;

    return (
        <div className="mb-4 animate-fade-in">
            {/* Header com contador e controle de expansão */}
            {notifications && notifications.length > 0 && (
                <div className="flex items-center justify-between mb-2 px-0.5">
                    <button
                        onClick={() => hasMultiple && setIsExpanded(!isExpanded)}
                        className={`flex items-center gap-2 text-left ${hasMultiple ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'}`}
                    >
                        <div className="relative">
                            <Bell size={16} className="text-indigo-600 dark:text-indigo-400" />
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                                {notifications.length}
                            </span>
                        </div>
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Lembretes ({notifications.length})
                        </span>
                        {hasMultiple && (
                            <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-md">
                                {isExpanded ? 'Recolher' : 'Ver todos'}
                            </span>
                        )}
                    </button>
                    {hasMultiple && (
                        <button
                            onClick={onDismissAll}
                            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            Dispensar todos
                        </button>
                    )}
                </div>
            )}

            {/* Lista de notificações (exibe todas se expandido, ou apenas a primeira/mais urgente se houver múltiplas) */}
            <div className="space-y-2">
                {notifications && (isExpanded || !hasMultiple ? notifications : [notifications[0]]).map((notification) => {
                    const styles = getUrgencyStyles(notification.urgency);
                    const Icon = getIcon(notification.type, notification.urgency);

                    return (
                        <div
                            key={notification.id}
                            className={`${styles.bg} ${styles.border} border rounded-xl p-3 flex items-center gap-3 transition-all hover:shadow-sm cursor-pointer group`}
                            onClick={() => handleClick(notification)}
                        >
                            {/* Ícone */}
                            <div className={`${styles.icon} p-2 rounded-lg shrink-0`}>
                                <Icon size={18} />
                            </div>

                            {/* Conteúdo */}
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-bold ${styles.text} truncate`}>
                                    {notification.title}
                                </div>
                                <div className={`text-xs ${styles.subtext} flex items-center gap-1`}>
                                    <Clock size={10} />
                                    {notification.message}
                                </div>
                            </div>

                            {/* Ações */}
                            <div className="flex items-center gap-1 shrink-0">
                                <ChevronRight
                                    size={16}
                                    className={`${styles.subtext} opacity-60 group-hover:opacity-100 transition-opacity`}
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDismiss(notification.id);
                                    }}
                                    className={`p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${styles.subtext}`}
                                    title="Dispensar"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Botão de ver mais notificações caso recolhido */}
            {hasMultiple && !isExpanded && (
                <button
                    onClick={() => setIsExpanded(true)}
                    className="w-full mt-1.5 py-1 text-center text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                >
                    + {notifications.length - 1} outro{notifications.length - 1 > 1 ? 's' : ''} lembrete{notifications.length - 1 > 1 ? 's' : ''} (toque para ver)
                </button>
            )}
        </div>
    );
});

NotificationBanner.displayName = 'NotificationBanner';

export default NotificationBanner;
